import { useAccount } from 'wagmi'
import { Address } from 'viem'
import { useMemo } from 'react'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { getSubAccountAddress, MAIN_ACCOUNT_ID, FALLBACK_SUBACCOUNT_RANGE } from '@/domain/euler/shared/subAccounts'
import {
  useSubaccountStore,
  getFirstAvailableSubaccountId,
  useSubaccountControllers,
} from '@/domain/euler/subaccounts'

export interface UseAvailableSubAccountParams {
  /** The liability vault address the user wants to borrow from */
  liabilityVault?: Address
  userAddress?: Address
  chainId?: number
  enabled?: boolean
}

export interface UseAvailableSubAccountResult {
  /** The next available sub-account ID that can be used for a new position with this vault */
  availableSubAccountId: number | null
  /** The sub-account address for the available ID */
  availableSubAccountAddress: Address | null
  /** Whether an existing sub-account is already using this liability vault */
  hasExistingPosition: boolean
  /** The sub-account ID of an existing position with this vault (if any) */
  existingSubAccountId: number | null
  /** Map of sub-account IDs to their controller vaults */
  subAccountControllers: Map<number, Address[]>
  /** True if all checked sub-accounts have controllers and no new position can be opened */
  allSubAccountsUsed: boolean
  isLoading: boolean
  error: Error | null
}

/**
 * Hook to find the next available sub-account for opening a new borrow position.
 *
 * Uses the global subaccount discovery cache for efficient lookups.
 * Only queries controllers for known active subaccounts instead of all 0-9.
 *
 * Since each sub-account can only have one controller (liability vault) at a time,
 * this hook finds:
 * 1. If the user already has a position with this liability vault (and which sub-account)
 * 2. The next available sub-account that has no controller enabled
 *
 * Usage:
 * - When opening a new position, use this to determine which subAccountId to use
 * - If hasExistingPosition is true, the user can add to their existing position
 * - Otherwise, use availableSubAccountId for a new isolated position
 */
export function useAvailableSubAccount({
  liabilityVault,
  userAddress,
  chainId = E_LEND_CHAIN_ID,
  enabled = true,
}: UseAvailableSubAccountParams): UseAvailableSubAccountResult {
  const { address: connectedAddress } = useAccount()
  const address = userAddress || connectedAddress

  // Get active subaccount IDs from the global discovery cache
  const { activeSubaccountIds, isDiscovering } = useSubaccountStore()

  // Combine fallback range (0-15) with discovered active IDs for consistency with other hooks
  // This ensures we detect positions that the dashboard shows (which also uses fallback range)
  const idsToQuery = useMemo(() => {
    const fallbackIds = Array.from({ length: FALLBACK_SUBACCOUNT_RANGE }, (_, i) => i)
    return [...new Set([...fallbackIds, ...activeSubaccountIds])]
  }, [activeSubaccountIds])

  // Query controllers for fallback range + active subaccounts
  const { subaccountControllers, isLoading: isLoadingControllers, error } = useSubaccountControllers({
    owner: address,
    subaccountIds: idsToQuery,
    chainId,
    enabled: enabled && !!address,
  })

  // Parse results and find available sub-account
  const result = useMemo(() => {
    if (!address) {
      return {
        availableSubAccountId: MAIN_ACCOUNT_ID,
        availableSubAccountAddress: null,
        hasExistingPosition: false,
        existingSubAccountId: null,
        subAccountControllers: new Map<number, Address[]>(),
        allSubAccountsUsed: false,
      }
    }

    let existingSubAccountId: number | null = null

    // Build set of IDs that actually have controllers (not just queried, but with active positions)
    const idsWithControllers: number[] = []

    // Check if any subaccount has this liability vault as controller
    for (const [subAccountId, controllers] of subaccountControllers) {
      if (controllers.length > 0) {
        idsWithControllers.push(subAccountId)
      }
      if (liabilityVault && controllers.some((c) => c.toLowerCase() === liabilityVault.toLowerCase())) {
        existingSubAccountId = subAccountId
      }
    }

    // Find first available subaccount (either a gap or next sequential)
    // Use the actual IDs that have controllers, not just the queried IDs
    const availableSubAccountId = getFirstAvailableSubaccountId(idsWithControllers)
    const allSubAccountsUsed = availableSubAccountId === -1

    const result = {
      availableSubAccountId: allSubAccountsUsed ? null : availableSubAccountId,
      availableSubAccountAddress: allSubAccountsUsed
        ? null
        : getSubAccountAddress(address, availableSubAccountId),
      hasExistingPosition: existingSubAccountId !== null,
      existingSubAccountId,
      subAccountControllers: subaccountControllers,
      allSubAccountsUsed,
    }

    return result
  }, [address, subaccountControllers, liabilityVault])

  const isLoading = isDiscovering || isLoadingControllers

  return {
    ...result,
    isLoading,
    error,
  }
}

/**
 * Simple utility hook to get the next available sub-account ID for any new position.
 * Uses the global discovery cache to find gaps or the next sequential ID (0-255).
 */
export function useNextAvailableSubAccountId({
  userAddress,
  chainId = E_LEND_CHAIN_ID,
  enabled = true,
}: Omit<UseAvailableSubAccountParams, 'liabilityVault'>): {
  subAccountId: number | null
  subAccountAddress: Address | null
  isLoading: boolean
} {
  const { availableSubAccountId, availableSubAccountAddress, isLoading } = useAvailableSubAccount({
    userAddress,
    chainId,
    enabled,
  })

  return {
    subAccountId: availableSubAccountId,
    subAccountAddress: availableSubAccountAddress,
    isLoading,
  }
}
